import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Grid, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Chip, Alert, CircularProgress, MenuItem, Select, InputLabel, FormControl, Tabs, Tab, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import { getTemplates, createTemplate, updateTemplate, deleteTemplate, setDefaultTemplate, TransactionalEmailTemplate, getTransactionalEmailSettings, updateTransactionalEmailSettings, TransactionalEmailSettings } from '../../../../firebase/services/transactionalEmailTemplateService';
import EmailTemplateDesigner from '../../../../components/email/EmailTemplateDesigner';
import { useLocationContext } from '../../../../contexts/LocationContext';

// Example types and variables for demonstration
const TEMPLATE_TYPES = [
  { type: 'receipt', label: 'Receipt', variables: ['customerName', 'amount', 'date', 'items'] },
  { type: 'reservation_confirmation', label: 'Reservation Confirmation', variables: ['customerName', 'reservationId', 'startDate', 'endDate', 'boatName', 'partySize'] },
  { type: 'cancellation', label: 'Cancellation', variables: ['customerName', 'reservationId', 'cancelDate'] },
];



const EMAIL_PROVIDERS = [
  { label: 'Gmail', value: 'gmail', smtpHost: 'smtp.gmail.com', smtpPort: 465, security: 'SSL/TLS', help: 'Gmail requires App Passwords and 2-Step Verification.' },
  { label: 'Google Workspace', value: 'google_workspace', smtpHost: 'smtp.gmail.com', smtpPort: 465, security: 'SSL/TLS', help: 'Google Workspace requires App Passwords and 2-Step Verification.' },
  { label: 'Outlook', value: 'outlook', smtpHost: 'smtp.office365.com', smtpPort: 587, security: 'STARTTLS', help: 'Outlook/Office 365 uses STARTTLS on port 587.' },
  { label: 'Yahoo', value: 'yahoo', smtpHost: 'smtp.mail.yahoo.com', smtpPort: 465, security: 'SSL/TLS', help: 'Yahoo requires App Passwords.' },
  { label: 'iCloud', value: 'icloud', smtpHost: 'smtp.mail.me.com', smtpPort: 587, security: 'STARTTLS', help: 'iCloud uses STARTTLS on port 587.' },
  { label: 'Zoho', value: 'zoho', smtpHost: 'smtp.zoho.com', smtpPort: 465, security: 'SSL/TLS', help: 'Zoho uses SSL/TLS on port 465.' },
  { label: 'AOL', value: 'aol', smtpHost: 'smtp.aol.com', smtpPort: 465, security: 'SSL/TLS', help: 'AOL uses SSL/TLS on port 465.' },
  { label: 'Yandex', value: 'yandex', smtpHost: 'smtp.yandex.com', smtpPort: 465, security: 'SSL/TLS', help: 'Yandex uses SSL/TLS on port 465.' },
  { label: 'GMX', value: 'gmx', smtpHost: 'mail.gmx.com', smtpPort: 465, security: 'SSL/TLS', help: 'GMX uses SSL/TLS on port 465.' },
  { label: 'Mail.com', value: 'mailcom', smtpHost: 'smtp.mail.com', smtpPort: 465, security: 'SSL/TLS', help: 'Mail.com uses SSL/TLS on port 465.' },
  { label: 'ProtonMail (Bridge)', value: 'protonmail', smtpHost: '127.0.0.1', smtpPort: 1025, security: 'None', help: 'ProtonMail requires the Bridge app.' },
  { label: 'Custom', value: 'custom', smtpHost: '', smtpPort: 587, security: '', help: 'Enter your own SMTP settings.' },
];

const TransactionalEmailTemplatesPage: React.FC = () => {
  const { location } = useLocationContext();
  const locationId = location?.id || '';
  const locationType = location?.type || '';

  // Add a separate tab state for Templates/Settings
  const [settingsTab, setSettingsTab] = useState(0); // 0 = Templates, 1 = Settings

  // Template state
  const [templates, setTemplates] = useState<TransactionalEmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<TransactionalEmailTemplate | null>(null);
  const [form, setForm] = useState({
    type: '',
    name: '',
    subject: '',
    body: '',
  });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<TransactionalEmailTemplate | null>(null);

  // Settings state
  const [settings, setSettings] = useState<TransactionalEmailSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [settingsSuccess, setSettingsSuccess] = useState(false);
  const [settingsForm, setSettingsForm] = useState<Partial<TransactionalEmailSettings>>({});

  // Fetch templates when location changes
  const fetchTemplates = async () => {
    if (!locationId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getTemplates(locationId);
      setTemplates(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load templates');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTemplates();
     
  }, [locationId]);

  // Fetch settings when location changes
  useEffect(() => {
    if (!locationId) return;
    setSettingsLoading(true);
    getTransactionalEmailSettings(locationId)
      .then(data => {
        setSettings(data);
        setSettingsForm(data || {});
        setSettingsLoading(false);
      })
      .catch(() => {
        setSettingsError('Failed to load settings');
        setSettingsLoading(false);
      });
  }, [locationId]);

  const openCreateDialog = (type: string) => {
    setEditing(null);
    setForm({
      type,
      name: '',
      subject: '',
      body: '',
    });
    setDialogOpen(true);
  };

  const openEditDialog = (template: TransactionalEmailTemplate) => {
    setEditing(template);
    setForm({
      type: template.type,
      name: template.name,
      subject: template.subject,
      body: template.body,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    console.log('Save button clicked. Current form state:', form);
    
    try {
      const templateData = {
        name: form.name,
        subject: form.subject,
        body: form.body,
        type: form.type,
        isDefault: editing?.isDefault || false,
        updatedAt: Date.now()
      };

      console.log('Saving template data (direct button save):', JSON.stringify(templateData, null, 2));

      if (editing) {
        console.log('Updating existing template:', editing.id);
        console.log('Original template data:', JSON.stringify(editing, null, 2));
        
        // Make sure we're sending the latest body content
        if (!templateData.body && document.querySelector('.tiptap')) {
          const editorContent = (document.querySelector('.tiptap') as HTMLElement)?.innerHTML || '';
          if (editorContent) {
            console.log('Captured latest editor content:', editorContent);
            templateData.body = editorContent;
          }
        }
        
        await updateTemplate(locationId, editing.id, templateData);
      } else {
        console.log('Creating new template');
        await createTemplate(locationId, templateData);
      }
      setDialogOpen(false);
      await fetchTemplates();
    } catch (err: any) {
      console.error('Error saving template:', err);
      setError(err.message || 'Failed to save template');
    }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await deleteTemplate(locationId, id);
      fetchTemplates();
    } catch (err: any) {
      setError(err.message || 'Failed to delete template');
    }
  };

  const handleSetDefault = async (type: string, id: string) => {
    try {
      await setDefaultTemplate(locationId, type, id);
      fetchTemplates();
    } catch (err: any) {
      setError(err.message || 'Failed to set default template');
    }
  };

  const handlePreview = (template: TransactionalEmailTemplate) => {
    setPreview(template);
  };

  const handleSaveSettings = async () => {
    setSettingsError(null);
    try {
      await updateTransactionalEmailSettings(locationId, settingsForm);
      setSettingsSuccess(true);
      getTransactionalEmailSettings(locationId).then(setSettings);
    } catch (err: any) {
      setSettingsError(err?.message || 'Failed to save settings');
    }
  };

  const createDefaultTemplates = async () => {
    if (!locationId || !locationType) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Create a basic reservation confirmation template
      await createTemplate(locationId, {
        type: 'reservation_confirmation',
        name: 'Reservation Confirmation',
        subject: 'Reservation Confirmation - {{boatName}}',
        body: `
          <h1>Reservation Confirmation</h1>
          <p>Dear {{customerName}},</p>
          <p>Thank you for your reservation. Here are your reservation details:</p>
          <ul>
            <li><strong>Reservation ID:</strong> {{reservationId}}</li>
            <li><strong>Boat:</strong> {{boatName}}</li>
            <li><strong>Start Date:</strong> {{startDate}}</li>
            <li><strong>End Date:</strong> {{endDate}}</li>
            <li><strong>Party Size:</strong> {{partySize}}</li>
            <li><strong>Total Amount:</strong> {{totalAmount}}</li>
          </ul>
          <p>If you have any questions or need to make changes to your reservation, please contact us.</p>
          <p>Best regards,<br>The Marina Team</p>
        `,
        isDefault: true
      });

      // Refresh templates
      await fetchTemplates();
    } catch (err: any) {
      setError(err.message || 'Failed to create default templates');
    }
    setLoading(false);
  };

  if (!locationId) {
    return (
      <Box maxWidth="lg" mx="auto" mt={4}>
        <Typography variant="h5">Loading location...</Typography>
      </Box>
    );
  }

  return (
    <Box maxWidth="lg" mx="auto" mt={4}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Transactional Email Templates</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={createDefaultTemplates}
          disabled={loading || !locationType}
        >
          Create Default Templates
        </Button>
      </Box>
      {/* Templates/Settings Tabs */}
      <Tabs value={settingsTab} onChange={(_, v) => setSettingsTab(v)} sx={{ mb: 3 }}>
        <Tab label="Templates" />
        <Tab label="Settings" />
      </Tabs>
      {settingsTab === 0 && (
        <Box>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {loading ? <CircularProgress /> : (
            <Box>
              {TEMPLATE_TYPES.map(typeInfo => {
                const typeTemplates = templates.filter(t => t.type === typeInfo.type);
                return (
                  <Box key={typeInfo.type} mb={4}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Typography variant="h6">{typeInfo.label}</Typography>
                      <Button variant="outlined" startIcon={<AddIcon />} onClick={() => openCreateDialog(typeInfo.type)}>
                        New Template
                      </Button>
                    </Box>
                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary">Available variables:</Typography>
                      {typeInfo.variables.map(v => <Chip key={v} label={`{{${v}}}`} size="small" sx={{ mr: 0.5, mb: 0.5 }} />)}
                    </Box>
                    {typeTemplates.length === 0 ? (
                      <Alert severity="info">No templates for this type.</Alert>
                    ) : (
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Subject</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {typeTemplates.map(template => (
                              <TableRow 
                                key={template.id}
                                sx={{ 
                                  backgroundColor: template.isDefault ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                }}
                              >
                                <TableCell>{template.name || '(No Name)'}</TableCell>
                                <TableCell>{template.subject}</TableCell>
                                <TableCell>
                                  {template.isDefault && (
                                    <Chip 
                                      label="Default" 
                                      color="primary" 
                                      size="small" 
                                      icon={<StarIcon />}
                                    />
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton onClick={() => handlePreview(template)} size="small">
                                    <span role="img" aria-label="Preview">👁️</span>
                                  </IconButton>
                                  <IconButton onClick={() => openEditDialog(template)} size="small">
                                    <EditIcon />
                                  </IconButton>
                                  <IconButton 
                                    onClick={() => handleDelete(template.id)} 
                                    disabled={template.isDefault}
                                    size="small"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                  <IconButton 
                                    onClick={() => handleSetDefault(template.type, template.id)}
                                    color={template.isDefault ? 'primary' : 'default'}
                                    size="small"
                                  >
                                    {template.isDefault ? <StarIcon /> : <StarBorderIcon />}
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
          {/* Create/Edit Dialog with EmailTemplateDesigner */}
          <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xl" fullWidth>
            <DialogTitle>{editing ? 'Edit Template' : 'New Template'}</DialogTitle>
            <DialogContent>
              <Box component="form" noValidate autoComplete="off" sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Template Name"
                  value={form.name}
                  onChange={(e) => {
                    setForm({ ...form, name: e.target.value });
                  }}
                  margin="normal"
                  required
                />
                <EmailTemplateDesigner
                  template={{
                    id: editing?.id || crypto.randomUUID(),
                    name: form.name,
                    subject: form.subject,
                    body: form.body,
                    variables: TEMPLATE_TYPES.find(t => t.type === form.type)?.variables || [],
                    createdAt: editing?.createdAt || Date.now(),
                    updatedAt: Date.now()
                  }}
                  onSave={async (updatedTemplate) => {
                    setForm({
                      ...form,
                      name: updatedTemplate.name,
                      subject: updatedTemplate.subject,
                      body: updatedTemplate.body
                    });
                    
                    // Save the template to Firebase
                    try {
                      const templateData = {
                        name: updatedTemplate.name,
                        subject: updatedTemplate.subject,
                        body: updatedTemplate.body,
                        type: form.type,
                        isDefault: editing?.isDefault || false,
                        updatedAt: Date.now()
                      };

                      if (editing) {
                        await updateTemplate(locationId, editing.id, templateData);
                      } else {
                        await createTemplate(locationId, templateData);
                      }
                      setDialogOpen(false);
                      await fetchTemplates();
                    } catch (err: any) {
                      console.error('Error saving template:', err);
                      setError(err.message || 'Failed to save template');
                    }
                  }}
                  onDelete={() => {
                    if (editing) handleDelete(editing.id);
                    setDialogOpen(false);
                  }}
                  onContentChange={(content) => {
                    setForm(prevForm => ({
                      ...prevForm,
                      body: content
                    }));
                  }}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleSave}
                disabled={saving}
                startIcon={<SaveIcon />}
              >
                {saving ? 'Saving...' : 'Save Template'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
      {settingsTab === 1 && (
        <Box>
          {settingsLoading ? <CircularProgress /> : (
            <Paper sx={{ p: 3, maxWidth: 600 }}>
              <Typography variant="h6" gutterBottom>Transactional Email Settings</Typography>
              {settingsError && <Alert severity="error" sx={{ mb: 2 }}>{settingsError}</Alert>}
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Default From Name"
                    fullWidth
                    value={settingsForm.defaultSenderName || ''}
                    onChange={e => setSettingsForm(f => ({ ...f, defaultSenderName: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Sender Email Address"
                    fullWidth
                    type="email"
                    value={settingsForm.defaultSenderEmail || ''}
                    onChange={e => setSettingsForm(f => ({ ...f, defaultSenderEmail: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Default Email Footer Text"
                    fullWidth
                    multiline
                    rows={2}
                    value={settingsForm.defaultFooter || ''}
                    onChange={e => setSettingsForm(f => ({ ...f, defaultFooter: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" gutterBottom>Branding Logo</Typography>
                  {/* You can add an ImageSelectorDialog here if needed */}
                  <TextField
                    label="Branding Logo URL"
                    fullWidth
                    value={settingsForm.brandingLogoUrl || ''}
                    onChange={e => setSettingsForm(f => ({ ...f, brandingLogoUrl: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Email Provider"
                    fullWidth
                    value={settingsForm.provider || ''}
                    onChange={e => {
                      const provider = EMAIL_PROVIDERS.find(p => p.value === e.target.value);
                      setSettingsForm(f => ({
                        ...f,
                        provider: provider?.value || '',
                        smtpHost: provider?.smtpHost || '',
                        smtpPort: provider?.smtpPort || 587,
                        security: provider?.security || '',
                      }));
                    }}
                    helperText="Select your email provider to auto-fill SMTP settings."
                    sx={{ mb: 2 }}
                  >
                    {EMAIL_PROVIDERS.map(provider => (
                      <MenuItem key={provider.value} value={provider.value}>{provider.label}</MenuItem>
                    ))}
                  </TextField>
                  {settingsForm.provider && EMAIL_PROVIDERS.find(p => p.value === settingsForm.provider)?.help && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      {EMAIL_PROVIDERS.find(p => p.value === settingsForm.provider)?.help}
                    </Alert>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="SMTP Host"
                    fullWidth
                    value={settingsForm.smtpHost || ''}
                    onChange={e => setSettingsForm(f => ({ ...f, smtpHost: e.target.value }))}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    label="SMTP Port"
                    type="number"
                    fullWidth
                    value={settingsForm.smtpPort || 587}
                    onChange={e => setSettingsForm(f => ({ ...f, smtpPort: parseInt(e.target.value) }))}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    label="Security (SSL/TLS/STARTTLS/None)"
                    fullWidth
                    value={settingsForm.security || ''}
                    onChange={e => setSettingsForm(f => ({ ...f, security: e.target.value }))}
                    sx={{ mb: 2 }}
                  />
                </Grid>
              </Grid>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                sx={{ mt: 3 }}
                onClick={handleSaveSettings}
              >
                Save Settings
              </Button>
              <Snackbar
                open={settingsSuccess}
                autoHideDuration={3000}
                onClose={() => setSettingsSuccess(false)}
                message="Settings saved"
              />
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TransactionalEmailTemplatesPage; 